import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Icon, IconName, Spinner, CardTitle } from '../../components/ui';

interface Stats {
  users: {
    total: number;
    active_subscriptions: number;
    by_tier: Array<{ name: string; count: number }>;
  };
  usage: {
    today: { characters: number; audio_files: number };
    this_month: { characters: number; audio_files: number };
  };
}

const TILE_TINTS = {
  accent: {
    border: '#3A3320',
    background: 'linear-gradient(150deg,rgba(var(--a-rgb),.15),#17171B 65%)',
  },
  green: {
    border: '#26262B',
    background: 'linear-gradient(150deg,rgba(46,139,87,.12),#17171B 65%)',
  },
  blue: {
    border: '#26262B',
    background: 'linear-gradient(150deg,rgba(31,143,224,.12),#17171B 65%)',
  },
} as const;

function StatTile({
  label,
  value,
  tint,
}: {
  label: string;
  value: string;
  tint: keyof typeof TILE_TINTS;
}) {
  const t = TILE_TINTS[tint];
  return (
    <div
      className="p-5 rounded-[14px] border"
      style={{ borderColor: t.border, background: t.background }}
    >
      <p className="m-0 text-mist-500 text-xs font-semibold uppercase tracking-[0.12em]">{label}</p>
      <p className="mt-2 mb-0 font-display font-extrabold text-white text-[28px] leading-none">
        {value}
      </p>
    </div>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    const result = await api.adminGetStats();
    if (result.success && result.data) {
      setStats(result.data);
    } else {
      setError(result.error?.message || 'Failed to load stats');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-[#F0928C] text-sm">{error}</p>
        <button onClick={loadStats} className="btn-ghost mt-4">
          Retry
        </button>
      </div>
    );
  }

  const byTier = stats?.users.by_tier || [];
  const maxTierCount = Math.max(1, ...byTier.map((t) => t.count));

  const quickActions: Array<{ icon: IconName; title: string; desc: string; onClick: () => void }> = [
    {
      icon: 'key',
      title: 'Configure APIs',
      desc: 'Manage API credentials',
      onClick: () => (window.location.hash = 'admin/credentials'),
    },
    {
      icon: 'users',
      title: 'Manage Users',
      desc: 'View and edit users',
      onClick: () => (window.location.hash = 'admin/users'),
    },
    {
      icon: 'tiers',
      title: 'Subscription Tiers',
      desc: 'Manage pricing plans',
      onClick: () => (window.location.hash = 'admin/tiers'),
    },
    {
      icon: 'refresh',
      title: 'Refresh Stats',
      desc: 'Reload dashboard data',
      onClick: loadStats,
    },
  ];

  return (
    <div className="fx flex flex-col gap-5">
      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile
          label="Total users"
          value={(stats?.users.total || 0).toLocaleString()}
          tint="accent"
        />
        <StatTile
          label="Active subscriptions"
          value={(stats?.users.active_subscriptions || 0).toLocaleString()}
          tint="green"
        />
        <StatTile
          label="Characters this month"
          value={(stats?.usage.this_month.characters || 0).toLocaleString()}
          tint="blue"
        />
        <StatTile
          label="Audio files this month"
          value={(stats?.usage.this_month.audio_files || 0).toLocaleString()}
          tint="accent"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Users by tier */}
        <div className="card">
          <CardTitle>Users by tier</CardTitle>
          <div className="mt-3">
            {byTier.map((tier, idx) => (
              <div key={idx} className="py-3 border-t border-ink-800">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-white text-[13.5px] font-semibold truncate">{tier.name}</span>
                  <span className="text-mist-400 text-xs font-mono flex-none">
                    {tier.count.toLocaleString()} users
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-ink-800 overflow-hidden">
                  <div
                    className="h-full grad-bar rounded-full"
                    style={{ width: `${Math.round((tier.count / maxTierCount) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
            {byTier.length === 0 && (
              <p className="text-center text-mist-500 text-sm py-8 m-0">No tier data available</p>
            )}
          </div>
        </div>

        {/* Activity today */}
        <div className="card">
          <CardTitle>Activity today</CardTitle>
          <div className="mt-3">
            <div className="flex items-center justify-between gap-3 py-3 border-t border-ink-800">
              <span className="text-mist-300 text-[13px]">Characters processed</span>
              <span className="text-white text-[13px] font-mono">
                {(stats?.usage.today.characters || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 py-3 border-t border-ink-800">
              <span className="text-mist-300 text-[13px]">Audio files generated</span>
              <span className="text-white text-[13px] font-mono">
                {(stats?.usage.today.audio_files || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 py-3 border-t border-ink-800">
              <span className="text-mist-300 text-[13px]">Characters this month</span>
              <span className="text-white text-[13px] font-mono">
                {(stats?.usage.this_month.characters || 0).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 py-3 border-t border-ink-800">
              <span className="text-mist-300 text-[13px]">Audio files this month</span>
              <span className="text-white text-[13px] font-mono">
                {(stats?.usage.this_month.audio_files || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="card">
        <CardTitle>Quick actions</CardTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
          {quickActions.map((action) => (
            <button
              key={action.title}
              onClick={action.onClick}
              className="lift p-4 text-left rounded-xl border border-ink-800 bg-ink-900 hover:border-ink-600 transition-colors"
            >
              <span className="block mb-2" style={{ color: 'var(--a-hi)' }}>
                <Icon name={action.icon} size={20} />
              </span>
              <p className="m-0 text-white text-[13.5px] font-semibold">{action.title}</p>
              <p className="mt-0.5 mb-0 text-mist-500 text-xs">{action.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
